import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, ShoppingBag, Heart, Sun, Zap, Cpu, Award, ShieldCheck, Truck, RotateCcw, Check, Play, ArrowRight, Info, CheckCircle2, MessageSquare, ThumbsUp, Upload, Image as ImageIcon } from 'lucide-react';
import {
  fetchProductByIdFromBackend,
  fetchProductsFromBackend,
  fetchWishlistFromBackend,
  toggleWishlistBackend,
  fetchProductReviewsFromBackend,
  submitProductReviewBackend,
  getProductImageUrl
} from '../services/api';

const ProductDetail = ({ cartItems = [], onAddToCart, onUpdateQuantity, onBuyNow }) => {
  const { id } = useParams();

  const [product, setProduct] = useState(null);
  const [loadingProduct, setLoadingProduct] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('Overview');
  const [isWishlist, setIsWishlist] = useState(false);
  const [selectedAddons, setSelectedAddons] = useState([]);

  const addonExtraTotal = selectedAddons.reduce((sum, item) => sum + Number(item.addon_price || 0), 0);

  // Get Logged In User Info for Autofill
  const savedUserJson = localStorage.getItem('user');
  let loggedInUserName = '';
  try {
    if (savedUserJson) {
      const parsedUser = JSON.parse(savedUserJson);
      loggedInUserName = parsedUser.name || parsedUser.full_name || '';
    }
  } catch (e) {}

  // New Review Form State & Real Reviews State
  const [newReviewer, setNewReviewer] = useState(loggedInUserName);
  const [newReviewText, setNewReviewText] = useState('');
  const [newRating, setNewRating] = useState(5);
  const [newReviewImage, setNewReviewImage] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewMsg, setReviewMsg] = useState(null);
  const [userReviews, setUserReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(5.0);
  const [visibleReviewsCount, setVisibleReviewsCount] = useState(5);
  const getVal = (val, fallback = "N/A") => {
    if (val === null || val === undefined || val === "" || String(val).trim() === "") {
      return fallback;
    }
    return val;
  };

  const loadReviewsData = async (targetId) => {
    const revData = await fetchProductReviewsFromBackend(targetId);
    if (revData) {
      setUserReviews(Array.isArray(revData.reviews) ? revData.reviews : []);
      setAverageRating(revData.average_rating !== undefined ? Number(revData.average_rating) : 5.0);
      setProduct(prev => (prev ? {
        ...prev,
        rating: revData.average_rating !== undefined ? Number(revData.average_rating) : 5.0,
        reviews_count: revData.total_reviews || 0
      } : prev));
    }
  };

  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      if (!id) {
        if (isMounted) {
          setNotFound(true);
          setLoadingProduct(false);
        }
        return;
      }

      setLoadingProduct(true);
      setNotFound(false);

      try {
        const item = await fetchProductByIdFromBackend(id);
        if (isMounted && item && (item.id || item.name)) {
          let parsedImages = [];
          if (item.gallery_images) {
            try {
              parsedImages = typeof item.gallery_images === 'string' ? JSON.parse(item.gallery_images) : item.gallery_images;
            } catch (e) {
              parsedImages = [];
            }
          }

          let finalImages = [];
          if (Array.isArray(parsedImages) && parsedImages.length > 0) {
            finalImages = [item.image_url, ...parsedImages].filter(Boolean);
          } else if (item.image_url) {
            finalImages = [item.image_url];
          } else {
            finalImages = ["/uploads/panel_72led.jpg"];
          }

          const dynamicProduct = {
            id: item.id,
            name: getVal(item.name),
            sku: getVal(item.sku),
            price: item.price ? parseFloat(item.price) : null,
            sale_price: item.sale_price ? parseFloat(item.sale_price) : null,
            stock: item.stock !== undefined && item.stock !== null ? parseInt(item.stock, 10) : null,
            led_chips_count: getVal(item.led_chips_count),
            actual_power: getVal(item.actual_power),
            spectrum: getVal(item.spectrum),
            irradiance: getVal(item.irradiance),
            dimensions: getVal(item.dimensions),
            weight_kg: item.weight_kg ? `${item.weight_kg} kg` : getVal(null),
            working_current: getVal(item.working_current),
            input_voltage: getVal(item.input_voltage, "100-240V AC"),
            lens_angle: getVal(item.lens_angle),
            control_interface: getVal(item.control_interface),
            flicker: getVal(item.flicker),
            dimmer: getVal(item.dimmer),
            shell_material: getVal(item.shell_material),
            shell_color: getVal(item.shell_color),
            frequency: getVal(item.frequency),
            remote_controller: getVal(item.remote_controller, "Support"),
            package_includes: getVal(item.package_includes),
            optional_addons: Array.isArray(item.optional_addons) ? item.optional_addons : [],
            description: getVal(item.description),
            rating: 5.0,
            reviews_count: 0,
            images: finalImages
          };

          setProduct(dynamicProduct);
          setSelectedImage(finalImages[0]);

          // Check if item was already in cart with selected addons
          if (cartItems && cartItems.length > 0) {
            const existingCartItem = cartItems.find(ci => Number(ci.id) === Number(id) || Number(ci.product_id) === Number(id));
            if (existingCartItem && Array.isArray(existingCartItem.selected_addons) && existingCartItem.selected_addons.length > 0) {
              setSelectedAddons(existingCartItem.selected_addons);
            }
          }

          await loadReviewsData(id);
        } else if (isMounted) {
          setNotFound(true);
        }
      } catch (err) {
        if (isMounted) setNotFound(true);
      } finally {
        if (isMounted) setLoadingProduct(false);
      }

      // Check if product is in User Wishlist
      try {
        const userWishlist = await fetchWishlistFromBackend();
        if (isMounted && Array.isArray(userWishlist)) {
          const isSaved = userWishlist.some(w => Number(w.id) === Number(id));
          setIsWishlist(isSaved);
        }
      } catch (e) {}
    };

    loadData();
    return () => { isMounted = false; };
  }, [id]);

  const handleToggleWishlist = async () => {
    const nextState = !isWishlist;
    setIsWishlist(nextState);
    if (product?.id) {
      await toggleWishlistBackend(product.id);
    }
  };

  if (loadingProduct) {
    return (
      <div style={{ background: '#ffffff', minHeight: '80vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        <div style={{ width: '40px', height: '40px', border: '3px solid #fee2e2', borderTopColor: '#dc2626', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginBottom: '16px' }} />
        <span style={{ fontSize: '15px', color: '#64748b', fontWeight: 600 }}>Loading panel specifications...</span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div style={{ background: '#ffffff', minHeight: '80vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: "'Plus Jakarta Sans', sans-serif", padding: '40px 24px', textAlign: 'center' }}>
        <h2 style={{ fontSize: '28px', fontWeight: 900, color: '#0f172a', marginBottom: '8px' }}>Product Not Found</h2>
        <p style={{ fontSize: '15px', color: '#64748b', maxWidth: '480px', marginBottom: '24px' }}>
          The red light therapy panel you are looking for does not exist or may have been updated.
        </p>
        <Link to="/shop" style={{ background: '#dc2626', color: '#ffffff', textDecoration: 'none', padding: '14px 32px', borderRadius: '9999px', fontSize: '14px', fontWeight: 800 }}>
          Browse Real Panels in Shop
        </Link>
      </div>
    );
  }

  const priceNum = Number(product.price || 0);
  const originalPrice = product.sale_price ? Number(product.sale_price) : priceNum;
  const savings = Math.max(0, originalPrice - priceNum);
  const handleImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewReviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddReview = async (e) => {
    e.preventDefault();
    if (!newReviewText.trim() || !product.id) return;
    setReviewMsg(null);

    try {
      setSubmittingReview(true);
      const res = await submitProductReviewBackend({
        product_id: product.id,
        user_name: newReviewer.trim() || "Valued Customer",
        rating: Number(newRating),
        comment: newReviewText.trim(),
        images: newReviewImage ? [newReviewImage] : []
      });

      if (res && res.success) {
        setNewReviewText('');
        setNewReviewer(loggedInUserName);
        setNewRating(5);
        setNewReviewImage('');
        setReviewMsg({ type: 'success', text: "Thank you! Your review has been submitted and published." });
        await loadReviewsData(product.id);
      } else {
        setReviewMsg({ type: 'error', text: res?.message || "Failed to post review. Please try again." });
      }
    } catch (err) {
      setReviewMsg({ type: 'error', text: "Error submitting review. Please try again." });
    } finally {
      setSubmittingReview(false);
    }
  };

  return (
    <div style={{ background: '#ffffff', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#0f172a' }}>
      
      {/* 1. TOP GUARANTEE ANNOUNCEMENT BAR */}
      <div style={{ background: '#ffffff', borderBottom: '1px solid #f1f5f9', padding: '8px 24px', fontSize: '12px', color: '#475569' }}>
        <div style={{ maxWidth: '1360px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px', flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><Truck size={14} color="#dc2626" /><strong>Free Shipping</strong> on all orders</span>
          <span style={{ color: '#cbd5e1' }}>|</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><ShieldCheck size={14} color="#dc2626" /><strong>2 Year Warranty</strong></span>
          <span style={{ color: '#cbd5e1' }}>|</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><RotateCcw size={14} color="#dc2626" /><strong>60 Day Returns</strong></span>
          <span style={{ color: '#cbd5e1' }}>|</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><Award size={14} color="#dc2626" /><strong>Dedicated Support</strong></span>
        </div>
      </div>

      <div className="product-detail-container" style={{ maxWidth: '1360px', margin: '0 auto', padding: '48px 56px 80px' }}>
        
        {/* Breadcrumb */}
        <div style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '28px' }}>
          <Link to="/" style={{ color: '#94a3b8', textDecoration: 'none' }}>Home</Link>
          <span style={{ margin: '0 8px' }}>&gt;</span>
          <Link to="/shop" style={{ color: '#94a3b8', textDecoration: 'none' }}>Shop</Link>
          <span style={{ margin: '0 8px' }}>&gt;</span>
          <span style={{ color: '#0f172a', fontWeight: 600 }}>{product.name}</span>
        </div>

        {/* 2. MAIN HERO SECTION (CLEAN SINGLE HIGH-RES PRODUCT STAGE) */}
        <div className="product-detail-hero-grid">
          
          {/* Main Stage Image with Wishlist Button Fitted on Top Right */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            <div style={{ position: 'relative', display: 'inline-block', maxWidth: '100%', width: '100%', background: '#f8fafc', borderRadius: '24px', padding: '24px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
              <button
                onClick={handleToggleWishlist}
                title="Add to Wishlist"
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '50%',
                  width: '42px',
                  height: '42px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: isWishlist ? '#dc2626' : '#94a3b8',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
                  zIndex: 10
                }}
              >
                <Heart size={20} fill={isWishlist ? "#dc2626" : "none"} color={isWishlist ? "#dc2626" : "#94a3b8"} />
              </button>

              <img
                src={selectedImage || (product.images && product.images[0]) || getProductImageUrl(product.image_url)}
                alt={product.name}
                style={{ maxHeight: '460px', maxWidth: '100%', objectFit: 'contain', display: 'block', margin: '0 auto' }}
                onError={(e) => { e.target.src = getProductImageUrl('/uploads/panel_144led_pro.jpg'); }}
              />
            </div>
          </div>

          {/* Right Product Details Column */}
          <div>
            <span style={{ background: '#dc2626', color: '#ffffff', fontSize: '10px', fontWeight: 900, letterSpacing: '0.1em', padding: '3px 12px', borderRadius: '9999px', textTransform: 'uppercase', display: 'inline-block', marginBottom: '12px' }}>
              MOST POPULAR
            </span>

            <h1 style={{ fontSize: '38px', fontWeight: 900, color: '#0f172a', letterSpacing: '-0.02em', marginBottom: '8px' }}>
              {product.name}
            </h1>

            <p style={{ fontSize: '16px', fontWeight: 700, color: '#475569', marginBottom: '12px' }}>
              {product.spectrum}
            </p>

            {/* Star Rating */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', gap: '2px', color: '#dc2626' }}>
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} fill="#dc2626" />
                ))}
              </div>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748b' }}>({product.reviews_count || 124} reviews)</span>
            </div>

            <p style={{ fontSize: '14.5px', color: '#475569', lineHeight: 1.6, marginBottom: '24px' }}>
              {product.description}
            </p>

            {/* 4 Technical Feature Badges */}
            <div className="product-tech-badges-grid">
              <div>
                <Sun size={20} color="#dc2626" style={{ margin: '0 auto 4px' }} />
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#0f172a' }}>660nm & 850nm</div>
                <div style={{ fontSize: '10.5px', color: '#64748b' }}>Dual Wavelengths</div>
              </div>

              <div>
                <Zap size={20} color="#dc2626" style={{ margin: '0 auto 4px' }} />
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#0f172a' }}>{product.actual_power}</div>
                <div style={{ fontSize: '10.5px', color: '#64748b' }}>High Power</div>
              </div>

              <div>
                <Cpu size={20} color="#dc2626" style={{ margin: '0 auto 4px' }} />
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#0f172a' }}>{product.led_chips_count} LEDs</div>
                <div style={{ fontSize: '10.5px', color: '#64748b' }}>Advanced Coverage</div>
              </div>

              <div>
                <Award size={20} color="#dc2626" style={{ margin: '0 auto 4px' }} />
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#0f172a' }}>Clinically</div>
                <div style={{ fontSize: '10.5px', color: '#64748b' }}>Proven</div>
              </div>
            </div>

            {/* Price Row & Add-on Calculation */}
            {(() => {
              const basePrice = Number(product.price || 0);
              const salePrice = product.sale_price ? Number(product.sale_price) : null;
              const hasDiscount = salePrice && salePrice < basePrice;
              const effectiveBase = hasDiscount ? salePrice : basePrice;
              const finalPrice = effectiveBase + addonExtraTotal;

              return (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '14px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '36px', fontWeight: 900, color: '#0f172a' }}>
                    £{finalPrice.toFixed(2)}
                  </span>
                  {addonExtraTotal > 0 && (
                    <span style={{ fontSize: '13px', fontWeight: 800, color: '#dc2626', background: '#fef2f2', padding: '3px 10px', borderRadius: '9999px' }}>
                      Base £{effectiveBase.toFixed(2)} + £{addonExtraTotal.toFixed(2)} Add-ons
                    </span>
                  )}
                  {hasDiscount && (
                    <span style={{ fontSize: '20px', color: '#94a3b8', textDecoration: 'line-through' }}>
                      £{(basePrice + addonExtraTotal).toFixed(2)}
                    </span>
                  )}
                </div>
              );
            })()}

            <div style={{ fontSize: '13px', fontWeight: 700, color: product.stock === 0 ? '#dc2626' : '#16a34a', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '20px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: product.stock === 0 ? '#dc2626' : '#16a34a', display: 'inline-block' }}></span>
              <span>{product.stock === 0 ? 'Out of Stock' : 'In Stock • Ready to Ship'}</span>
            </div>

            {/* OPTIONAL ADD-ONS CHECKLIST SECTION (Only rendered if product has valid add-ons) */}
            {Array.isArray(product.optional_addons) && product.optional_addons.filter(a => a.addon_name && a.addon_name.trim() !== '').length > 0 && (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px', marginBottom: '24px' }}>
                <div style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a', marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Optional Upgrades & Add-Ons</span>
                  <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>Select options to include</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '220px', overflowY: 'auto', paddingRight: '4px' }}>
                  {product.optional_addons.map((addon, idx) => {
                    const isSelected = selectedAddons.some(a => a.addon_name === addon.addon_name);
                    const priceVal = Number(addon.addon_price || 0);
                    return (
                      <label key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: isSelected ? '#fef2f2' : '#ffffff', border: isSelected ? '1px solid #fca5a5' : '1px solid #cbd5e1', borderRadius: '10px', cursor: 'pointer', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedAddons([...selectedAddons, addon]);
                              } else {
                                setSelectedAddons(selectedAddons.filter(a => a.addon_name !== addon.addon_name));
                              }
                            }}
                            style={{ width: '16px', height: '16px', accentColor: '#dc2626', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '13px', fontWeight: isSelected ? 700 : 600, color: '#1e293b' }}>{addon.addon_name}</span>
                        </div>
                        <span style={{ fontSize: '13px', fontWeight: 800, color: '#dc2626' }}>
                          +{priceVal > 0 ? `£${priceVal.toFixed(2)}` : 'Included'}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Purchase Controls Row */}
            {(() => {
              const maxStock = (product.stock !== null && product.stock !== undefined && !isNaN(product.stock)) ? Number(product.stock) : 99;
              const isOutOfStock = maxStock <= 0;

              const existingInCart = cartItems.find((ci) => Number(ci.id) === Number(product.id) || Number(ci.product_id) === Number(product.id));
              const currentCartQty = existingInCart ? (existingInCart.quantity || 1) : 0;

              return (
                <div style={{ display: 'flex', gap: '14px', marginBottom: '28px', flexWrap: 'wrap', alignItems: 'center' }}>
                  
                  {/* IF ITEM IS ALREADY IN CART: SHOW - N IN CART + SELECTOR (HIDE ADD TO CART BUTTON) */}
                  {currentCartQty > 0 ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '9999px', padding: '8px 18px', minWidth: '220px' }}>
                      <button
                        onClick={() => onUpdateQuantity && onUpdateQuantity(product.id, -1, maxStock)}
                        style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#ffffff', border: '1px solid #fecaca', color: '#dc2626', fontWeight: 900, fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        title="Decrease quantity in cart"
                      >
                        -
                      </button>
                      
                      <span style={{ fontSize: '15px', fontWeight: 900, color: '#dc2626', padding: '0 12px' }}>
                        {currentCartQty} in Cart
                      </span>

                      <button
                        onClick={() => onUpdateQuantity && onUpdateQuantity(product.id, 1, maxStock)}
                        disabled={currentCartQty >= maxStock}
                        style={{ width: '32px', height: '32px', borderRadius: '50%', background: currentCartQty >= maxStock ? '#f1f5f9' : '#dc2626', border: 'none', color: currentCartQty >= maxStock ? '#94a3b8' : '#ffffff', fontWeight: 900, fontSize: '18px', cursor: currentCartQty >= maxStock ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        title="Increase quantity in cart"
                      >
                        +
                      </button>
                    </div>
                  ) : (
                    /* IF ITEM IS NOT IN CART: SHOW ONLY ADD TO CART BUTTON (HIDE + - SELECTOR) */
                    <button
                      className="btn-red-pill"
                      disabled={isOutOfStock}
                      style={{ flex: 1, padding: '14px 32px', fontSize: '15px', justifyContent: 'center', opacity: isOutOfStock ? 0.6 : 1, cursor: isOutOfStock ? 'not-allowed' : 'pointer' }}
                      onClick={() => {
                        if (!isOutOfStock && onAddToCart) {
                          const baseP = (product.sale_price && Number(product.sale_price) < Number(product.price)) ? Number(product.sale_price) : Number(product.price || 0);
                          onAddToCart({
                            ...product,
                            price: baseP + addonExtraTotal,
                            selected_addons: selectedAddons,
                            quantity: 1
                          });
                        }
                      }}
                    >
                      <ShoppingBag size={18} />
                      <span>{isOutOfStock ? 'Out of Stock' : `Add to Cart ${addonExtraTotal > 0 ? `(£${(((product.sale_price && Number(product.sale_price) < Number(product.price)) ? Number(product.sale_price) : Number(product.price || 0)) + addonExtraTotal).toFixed(2)})` : ''}`}</span>
                    </button>
                  )}

                  {/* BUY NOW BUTTON */}
                  {!isOutOfStock && currentCartQty <= 0 && onBuyNow && (
                    <button
                      onClick={() => {
                        const baseP = (product.sale_price && Number(product.sale_price) < Number(product.price)) ? Number(product.sale_price) : Number(product.price || 0);
                        onBuyNow({
                          ...product,
                          price: baseP + addonExtraTotal,
                          selected_addons: selectedAddons,
                          quantity: 1
                        });
                      }}
                      style={{ border: '1px solid #dc2626', background: '#ffffff', color: '#dc2626', borderRadius: '9999px', padding: '14px 28px', fontSize: '14.5px', fontWeight: 800, cursor: 'pointer' }}
                    >
                      Buy Now
                    </button>
                  )}

                  {/* WISHLIST BUTTON */}
                  <button
                    onClick={handleToggleWishlist}
                    style={{ border: '1px solid #cbd5e1', background: '#ffffff', borderRadius: '9999px', padding: '14px 20px', fontSize: '13.5px', fontWeight: 700, color: '#0f172a', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <Heart size={16} fill={isWishlist ? "#dc2626" : "none"} color={isWishlist ? "#dc2626" : "#0f172a"} />
                    <span>{isWishlist ? 'Saved' : 'Add to Wishlist'}</span>
                  </button>

                </div>
              );
            })()}

            {/* 3 Guarantees */}
            <div style={{ display: 'flex', gap: '20px', borderTop: '1px solid #e2e8f0', paddingTop: '20px', fontSize: '12px', color: '#64748b' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Truck size={16} color="#dc2626" /><span><strong>Free Shipping</strong> On all orders</span></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><ShieldCheck size={16} color="#dc2626" /><span><strong>2 Year Warranty</strong> Peace of mind</span></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><RotateCcw size={16} color="#dc2626" /><span><strong>60 Day Returns</strong> Hassle free</span></div>
            </div>

          </div>

        </div>

        {/* 3. HIGHLIGHT PILL BANNER */}
        <div className="product-highlight-pill-banner">
          <span style={{ fontWeight: 800, color: '#dc2626' }}>Experience the Power of Red Light Therapy</span>
          <span className="banner-divider" style={{ color: '#fecaca' }}>|</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Zap size={16} color="#dc2626" /> Faster Recovery</span>
          <span className="banner-divider" style={{ color: '#fecaca' }}>|</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Sun size={16} color="#dc2626" /> Healthier Skin</span>
          <span className="banner-divider" style={{ color: '#fecaca' }}>|</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Zap size={16} color="#dc2626" /> More Energy</span>
          <span className="banner-divider" style={{ color: '#fecaca' }}>|</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Award size={16} color="#dc2626" /> Better Sleep</span>
        </div>

        {/* 4. DYNAMIC WORKING TABS NAVBAR */}
        <div className="product-tabs-navbar">
          {['Overview', 'Specifications', 'How It Works', 'Reviews'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                background: 'none',
                border: 'none',
                padding: '12px 0',
                fontSize: '15px',
                fontWeight: activeTab === tab ? 800 : 600,
                color: activeTab === tab ? '#dc2626' : '#64748b',
                borderBottom: activeTab === tab ? '3px solid #dc2626' : '3px solid transparent',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              {tab} {tab === 'Reviews' ? `(${userReviews.length})` : ''}
            </button>
          ))}
        </div>

        {/* TAB 1: OVERVIEW */}
        {activeTab === 'Overview' && (
          <div>
            <div className="tab-overview-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px', alignItems: 'center', marginBottom: '64px' }}>
              <div style={{ background: '#090a0f', borderRadius: '24px', padding: '40px', color: '#ffffff', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '240px', height: '240px', background: 'radial-gradient(circle, rgba(220,38,38,0.5) 0%, transparent 70%)', filter: 'blur(30px)' }}></div>
                <h3 style={{ fontSize: '32px', fontWeight: 900, lineHeight: 1.15, marginBottom: '20px' }}>
                  Advanced<br />
                  <span style={{ color: '#ef4444' }}>Red Light</span><br />
                  Technology
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', color: '#cbd5e1' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Check size={16} color="#ef4444" /><span>Deeper tissue penetration</span></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Check size={16} color="#ef4444" /><span>Faster recovery</span></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Check size={16} color="#ef4444" /><span>Visible skin benefits</span></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Check size={16} color="#ef4444" /><span>Natural wellness support</span></div>
                </div>
              </div>

              <div>
                <h2 style={{ fontSize: '32px', fontWeight: 900, color: '#0f172a', marginBottom: '16px' }}>
                  Why Choose {product.name}?
                </h2>
                <p style={{ fontSize: '15px', color: '#64748b', lineHeight: 1.7, marginBottom: '28px' }}>
                  Engineered with dual-wavelength technology ({product.spectrum}), the {product.name} delivers powerful, natural and non-invasive light therapy that helps your body heal, recover and perform at its best.
                </p>

                <div className="overview-benefits-boxes" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                  <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', textAlign: 'center', border: '1px solid #e2e8f0' }}>
                    <Sun size={22} color="#dc2626" style={{ margin: '0 auto 6px' }} />
                    <div style={{ fontSize: '12.5px', fontWeight: 800, color: '#0f172a' }}>Dual Wavelength</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>660nm & 850nm</div>
                  </div>

                  <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', textAlign: 'center', border: '1px solid #e2e8f0' }}>
                    <Zap size={22} color="#dc2626" style={{ margin: '0 auto 6px' }} />
                    <div style={{ fontSize: '12.5px', fontWeight: 800, color: '#0f172a' }}>{product.actual_power} Power</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>Deep Penetration</div>
                  </div>

                  <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', textAlign: 'center', border: '1px solid #e2e8f0' }}>
                    <Cpu size={22} color="#dc2626" style={{ margin: '0 auto 6px' }} />
                    <div style={{ fontSize: '12.5px', fontWeight: 800, color: '#0f172a' }}>{product.led_chips_count} LEDs</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>Full Body Coverage</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        
        {activeTab === 'Specifications' && (
          <div style={{ marginBottom: '64px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 900, color: '#0f172a', marginBottom: '20px' }}>
              Full Technical Specifications
            </h2>

            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', overflow: 'hidden', overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: '500px', borderCollapse: 'collapse', fontSize: '14px', color: '#334155' }}>
                <tbody>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '14px 24px', fontWeight: 700, width: '30%', color: '#0f172a' }}>Wavelength Spectrum</td>
                    <td style={{ padding: '14px 24px' }}>{product.spectrum}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '14px 24px', fontWeight: 700, color: '#0f172a' }}>LED Count</td>
                    <td style={{ padding: '14px 24px' }}>{product.led_chips_count}</td>
                  </tr>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '14px 24px', fontWeight: 700, color: '#0f172a' }}>Actual Power Consumption</td>
                    <td style={{ padding: '14px 24px' }}>{product.actual_power}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '14px 24px', fontWeight: 700, color: '#0f172a' }}>Input Voltage</td>
                    <td style={{ padding: '14px 24px' }}>{product.input_voltage}</td>
                  </tr>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '14px 24px', fontWeight: 700, color: '#0f172a' }}>Working Current</td>
                    <td style={{ padding: '14px 24px' }}>{product.working_current}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '14px 24px', fontWeight: 700, color: '#0f172a' }}>Frequency</td>
                    <td style={{ padding: '14px 24px' }}>{product.frequency}</td>
                  </tr>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '14px 24px', fontWeight: 700, color: '#0f172a' }}>Irradiance Optics</td>
                    <td style={{ padding: '14px 24px' }}>{product.irradiance}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '14px 24px', fontWeight: 700, color: '#0f172a' }}>Lens Angle</td>
                    <td style={{ padding: '14px 24px' }}>{product.lens_angle}</td>
                  </tr>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '14px 24px', fontWeight: 700, color: '#0f172a' }}>Control Interface</td>
                    <td style={{ padding: '14px 24px' }}>{product.control_interface}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '14px 24px', fontWeight: 700, color: '#0f172a' }}>Flicker & Dimmer Control</td>
                    <td style={{ padding: '14px 24px' }}>{product.flicker} • {product.dimmer}</td>
                  </tr>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '14px 24px', fontWeight: 700, color: '#0f172a' }}>Casing Material & Color</td>
                    <td style={{ padding: '14px 24px' }}>{product.shell_material} • {product.shell_color}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '14px 24px', fontWeight: 700, color: '#0f172a' }}>Dimensions & Weight</td>
                    <td style={{ padding: '14px 24px' }}>{product.dimensions} • {product.weight_kg}</td>
                  </tr>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '14px 24px', fontWeight: 700, color: '#0f172a' }}>Remote Controller</td>
                    <td style={{ padding: '14px 24px' }}>{product.remote_controller}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '14px 24px', fontWeight: 700, color: '#0f172a' }}>Package Included Items</td>
                    <td style={{ padding: '14px 24px' }}>{product.package_includes}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 3: HOW IT WORKS */}
        {activeTab === 'How It Works' && (
          <div style={{ marginBottom: '64px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 900, color: '#0f172a', marginBottom: '16px' }}>
              How Red Light Therapy Works
            </h2>
            <p style={{ fontSize: '15px', color: '#475569', lineHeight: 1.7, marginBottom: '32px', maxWidth: '800px' }}>
              Photobiomodulation uses precise 660nm Red and 850nm Near-Infrared light wavelengths to stimulate cytochrome c oxidase inside cell mitochondria. This boosts ATP cellular energy production, increases blood circulation, and accelerates natural repair.
            </p>

            <div className="tab-how-it-works-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#fef2f2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, marginBottom: '16px' }}>1</div>
                <h3 style={{ fontSize: '16.5px', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>Position & Distance</h3>
                <p style={{ fontSize: '13.5px', color: '#64748b', lineHeight: 1.6, margin: 0 }}>Position the panel 6 to 12 inches away from the target treatment area.</p>
              </div>

              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#fef2f2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, marginBottom: '16px' }}>2</div>
                <h3 style={{ fontSize: '16.5px', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>Session Duration</h3>
                <p style={{ fontSize: '13.5px', color: '#64748b', lineHeight: 1.6, margin: 0 }}>Enjoy a 10 to 20 minute session daily or 3-5 times per week for optimal results.</p>
              </div>

              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#fef2f2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, marginBottom: '16px' }}>3</div>
                <h3 style={{ fontSize: '16.5px', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>Recovery & Energy</h3>
                <p style={{ fontSize: '13.5px', color: '#64748b', lineHeight: 1.6, margin: 0 }}>Experience reduced muscle soreness, enhanced skin tone, and deep systemic recovery.</p>
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: REVIEWS & WRITE A REVIEW FORM */}
        {activeTab === 'Reviews' && (
          <div style={{ marginBottom: '64px' }}>
            <div className="tab-reviews-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px', alignItems: 'start' }}>
              
              {/* Left Customer Reviews List */}
              <div>
                <h2 style={{ fontSize: '28px', fontWeight: 900, color: '#0f172a', marginBottom: '20px' }}>
                  Verified Reviews ({userReviews.length})
                </h2>

                {userReviews.length === 0 ? (
                  <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '32px', textAlign: 'center', color: '#64748b' }}>
                    No reviews yet for this product. Be the first to share your experience!
                  </div>
                ) : (
                  <div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {userReviews.slice(0, visibleReviewsCount).map((rev) => (
                        <div key={rev.id} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '16px 20px', boxShadow: '0 2px 6px rgba(0,0,0,0.015)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a' }}>{rev.user_name || rev.author || 'Valued Customer'}</span>
                            <span style={{ fontSize: '11.5px', color: '#94a3b8' }}>
                              {rev.created_at ? new Date(rev.created_at).toLocaleDateString() : rev.date || 'Recently'}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: '2px', color: '#dc2626', marginBottom: '8px' }}>
                            {[...Array(Math.round(Number(rev.rating || 5)))].map((_, i) => (
                              <Star key={i} size={13} fill="#dc2626" />
                            ))}
                          </div>
                          {rev.title && <div style={{ fontSize: '13.5px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>{rev.title}</div>}
                          <p style={{ fontSize: '13px', color: '#475569', lineHeight: 1.5, margin: 0 }}>
                            "{rev.comment}"
                          </p>

                          {/* Customer Attached Photos */}
                          {Array.isArray(rev.images) && rev.images.length > 0 && (
                            <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                              {rev.images.map((img, idx) => (
                                <img key={idx} src={img} alt="Review attachment" style={{ width: '52px', height: '52px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #cbd5e1' }} />
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* PAGINATION / VIEW MORE BUTTONS */}
                    {userReviews.length > 5 && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '24px' }}>
                        {visibleReviewsCount < userReviews.length && (
                          <button
                            onClick={() => setVisibleReviewsCount(prev => prev + 5)}
                            style={{
                              background: '#ffffff',
                              border: '1px solid #cbd5e1',
                              borderRadius: '9999px',
                              padding: '10px 24px',
                              fontSize: '13px',
                              fontWeight: 800,
                              color: '#0f172a',
                              cursor: 'pointer',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.03)'
                            }}
                          >
                            View More Reviews ({userReviews.length - visibleReviewsCount} remaining)
                          </button>
                        )}

                        {visibleReviewsCount > 5 && (
                          <button
                            onClick={() => setVisibleReviewsCount(5)}
                            style={{
                              background: 'none',
                              border: 'none',
                              fontSize: '13px',
                              fontWeight: 800,
                              color: '#dc2626',
                              cursor: 'pointer',
                              padding: '10px'
                            }}
                          >
                            Show Less
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right Write a Review Form */}
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '32px' }}>
                <h3 style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', marginBottom: '16px' }}>Write a Review</h3>

                {reviewMsg && (
                  <div style={{
                    padding: '12px 16px',
                    borderRadius: '10px',
                    marginBottom: '16px',
                    fontSize: '13.5px',
                    fontWeight: 700,
                    background: reviewMsg.type === 'success' ? '#f0fdf4' : '#fef2f2',
                    border: reviewMsg.type === 'success' ? '1px solid #bbf7d0' : '1px solid #fecaca',
                    color: reviewMsg.type === 'success' ? '#15803d' : '#b91c1c'
                  }}>
                    {reviewMsg.text}
                  </div>
                )}

                <form onSubmit={handleAddReview} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', display: 'block', marginBottom: '6px' }}>Your Name</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Sarah Jenkins"
                      value={newReviewer}
                      onChange={(e) => setNewReviewer(e.target.value)}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', display: 'block', marginBottom: '8px' }}>Rating</label>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', cursor: 'pointer' }}>
                      {[1, 2, 3, 4, 5].map((starVal) => (
                        <Star
                          key={starVal}
                          size={24}
                          onClick={() => setNewRating(starVal)}
                          fill={starVal <= newRating ? "#dc2626" : "#e2e8f0"}
                          color={starVal <= newRating ? "#dc2626" : "#cbd5e1"}
                          style={{ transition: 'transform 0.15s ease' }}
                        />
                      ))}
                      <span style={{ fontSize: '13px', fontWeight: 800, color: '#dc2626', marginLeft: '6px' }}>
                        {newRating} / 5 Stars
                      </span>
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', display: 'block', marginBottom: '6px' }}>Your Review</label>
                    <textarea
                      required
                      rows={4}
                      placeholder="Share your experience with this red light therapy panel..."
                      value={newReviewText}
                      onChange={(e) => setNewReviewText(e.target.value)}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', fontFamily: 'inherit' }}
                    ></textarea>
                  </div>

                  <div>
                    <label style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', display: 'block', marginBottom: '6px' }}>
                      Attach Photo (Optional)
                    </label>
                    
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                      padding: '14px 20px',
                      borderRadius: '12px',
                      border: '2px dashed #cbd5e1',
                      background: '#ffffff',
                      color: '#0f172a',
                      fontSize: '13.5px',
                      fontWeight: 800,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
                    }}>
                      <Upload size={18} color="#dc2626" />
                      <span>{newReviewImage ? 'Change Photo' : 'Upload Review Photo'}</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                      />
                    </label>

                    {newReviewImage && (
                      <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '10px 14px' }}>
                        <img src={newReviewImage} alt="Review Preview" style={{ width: '48px', height: '48px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #cbd5e1' }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '12.5px', fontWeight: 800, color: '#0f172a' }}>Photo Selected</div>
                          <div style={{ fontSize: '11px', color: '#16a34a', fontWeight: 700 }}>Ready to upload</div>
                        </div>
                        <button type="button" onClick={() => setNewReviewImage('')} style={{ background: '#fef2f2', border: '1px solid #fee2e2', color: '#dc2626', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', fontWeight: 800, cursor: 'pointer' }}>Remove</button>
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={submittingReview}
                    className="btn-red-pill"
                    style={{ justifyContent: 'center', padding: '12px', opacity: submittingReview ? 0.7 : 1 }}
                  >
                    {submittingReview ? 'Submitting...' : 'Submit Review'}
                  </button>
                </form>
              </div>

            </div>
          </div>
        )}

        {/* 5. 2-BOX BOTTOM INFO CARDS (SPECIFICATIONS + WHAT'S INCLUDED) */}
        <div className="product-bottom-info-grid">
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a', marginBottom: '16px' }}>Specifications</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13.5px', color: '#475569' }}>
              <div>⚙️ <strong>LEDs:</strong> {product.led_chips_count}</div>
              <div>☀️ <strong>Power:</strong> {product.actual_power}</div>
              <div>🌐 <strong>Wavelengths:</strong> {product.spectrum}</div>
              <div>📏 <strong>Dimensions:</strong> {product.dimensions}</div>
              <div>⚖️ <strong>Weight:</strong> {product.weight_kg || 6.8} kg</div>
            </div>
          </div>

          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a', marginBottom: '16px' }}>What's Included</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13.5px', color: '#475569' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#dc2626" /><span>{product.name}</span></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#dc2626" /><span>Power Cable with Plug</span></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#dc2626" /><span>Eye Protection Goggles & User Manual</span></div>
              {product.remote_controller && product.remote_controller !== 'N/A' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#dc2626" /><span>Touch Screen Control Interface</span></div>
              )}
            </div>
          </div>
        </div>

        {/* 6. UPGRADE YOUR WELLNESS BOTTOM BANNER */}
        <div
          className="product-upgrade-wellness-banner"
          style={{
            background: 'linear-gradient(90deg, #991b1b 0%, #dc2626 50%, #b91c1c 100%)',
            borderRadius: '24px',
            padding: '36px 40px',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '24px',
            flexWrap: 'wrap',
            boxShadow: '0 20px 40px -10px rgba(220, 38, 38, 0.4)',
            boxSizing: 'border-box',
            width: '100%'
          }}
        >
          <div style={{ maxWidth: '100%' }}>
            <h2 style={{ fontSize: '30px', fontWeight: 900, lineHeight: 1.15, marginBottom: '6px', wordBreak: 'break-word' }}>
              Upgrade Your Wellness.
            </h2>
            <p style={{ fontSize: '16px', fontWeight: 600, color: '#fecaca', margin: 0 }}>
              Feel Better. Recover Faster.
            </p>
          </div>

          <Link
            to="/shop"
            className="upgrade-wellness-btn"
            style={{
              background: '#ffffff',
              color: '#dc2626',
              border: 'none',
              borderRadius: '9999px',
              padding: '14px 32px',
              fontSize: '14.5px',
              fontWeight: 900,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              textDecoration: 'none',
              boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
              maxWidth: '100%',
              boxSizing: 'border-box',
              flexShrink: 0
            }}
          >
            <span>Explore All Panels</span>
            <ArrowRight size={18} />
          </Link>
        </div>

      </div>
    </div>
  );
};

export default ProductDetail;